import { useRef } from "react"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useParams } from "react-router-dom"
import { isWeightable } from "@/lib/utils"

const OrderDetails = () => {
  const { id } = useParams()
  const printRef = useRef(null)

  const { data: response, isLoading } = useCustomQuery(
    ["invoice-detail", id],
    `order/${id}`
  )

  if (isLoading) {
    return <div>Loading...</div>
  }

  const order = response?.data
  console.log(order)

  const handlePrint = () => {
    if (!printRef.current) return

    const printContents = printRef.current.innerHTML
    const originalContents = document.body.innerHTML

    // Replace the body content with printable content
    document.body.innerHTML = printContents
    window.print()

    // Restore original body content after print
    document.body.innerHTML = originalContents
    window.location.reload() // Reload to reapply the React application
  }

  // Calculate VAT amount (reverse from total)
  const subtotalBeforeVat = order.total_amount / 1.13
  const vatAmount = order.total_amount - subtotalBeforeVat

  // Calculate total quantity sold
  const totalQuantitySold = order.order_items.reduce(
    (acc, item) => acc + item.quantity,
    0
  )

  return (
    <>
      <div
        ref={printRef}
        className="p-4 font-mono text-sm text-black bg-white max-w-sm mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="font-bold text-2xl">
            {order.store.brand_name ? order.store.brand_name : order.store.name}
          </h2>
          <p className="text-[17px]">{order.store.phone}</p>
          <p className="text-[17px]">{order.store.name}</p>
          <p className="text-[17px]">{order.store.location}</p>
        </div>

        {/* Transaction Details */}
        <div>
          <p className="text-[15px]">Invoice Number: {order.code}</p>
          <p className="text-[15px]">Invoice To: {order.customer.name}</p>
          <p className="text-[15px]">Mobile: {order.customer.phone}</p>
          {order.is_split_payment && order.split_payments && order.split_payments.length > 0 ? (
            <div>
              <p className="text-[15px] font-bold">Payment Methods:</p>
              {order.split_payments.map((payment, index) => (
                <p key={index} className="text-[15px]">
                  {payment.display_name || payment.payment_method_name}: Rs. {payment.amount?.toFixed(2)}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[15px]">Method: {order.payment_method}</p>
          )}
          <p className="text-[15px]">
            Date: {new Date(order.order_date).toLocaleDateString()}{" "}
          </p>
          <p className="text-[15px]">
            Time: {new Date(order.order_date).toLocaleTimeString()}
          </p>
        </div>

        {/* Product List */}
        <div className="border-t border-b py-2 mb-4">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="text-left text-[14px]">Item</td>
                <td className="text-center text-[14px]">Qty</td>
                {isWeightable() && (
                  <td className="text-center text-[14px]">Weight</td>
                )}
                <td className="text-center text-[14px]">Price</td>
                <td className="text-center text-[14px]">Dis</td>
                <td className="text-center text-[14px]">Total</td>
              </tr>
              {order.order_items.map((item, index) => {
                const discountAmount = (item.sell_price * item.discount) / 100
                const discountedPrice = item.sell_price - discountAmount
                return (
                  <tr key={index}>
                    <td className="text-[13px]">{item.sku}</td>
                    <td className="text-center text-[13px]">{item.quantity}</td>
                    {isWeightable() && (
                      <td className="text-center text-[13px]">
                        {item.weight}{" "}
                      </td>
                    )}
                    <td className="text-center text-[13px]">
                      {item.sell_price?.toFixed(2)}
                    </td>
                    <td className="text-center text-[13px]">
                      {discountAmount?.toFixed(2)}
                    </td>
                    <td className="text-center text-[13px]">
                      Rs. {(discountedPrice * item.quantity)?.toFixed(2)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Total Discount */}
        <div className="text-right">
          <p className="font-bold text-[13px]">
            Discount %: {order.discount_percentage?.toFixed(2)}
          </p>
        </div>

        {/* VAT Section */}
        {/* <div className="text-right">
          <p className="font-bold text-[13px]">
            VAT (13%): Rs. {vatAmount?.toFixed(2)}
          </p>
        </div> */}

        {/* Total Section */}
        <div className="text-right">
          <p className="font-bold text-[13px]">
            TOTAL: Rs.{" "}
            {Number(order.total_amount) + Number(order.final_discount)}
          </p>
        </div>

        <div className="text-right">
          <p className="font-bold text-[13px]">
            DISCOUNT After VAT: Rs. {Number(order.final_discount)}
          </p>
        </div>

        <div className="text-right">
          <p className="font-bold text-[13px]">
            GRAND TOTAL: Rs. {order.total_amount?.toFixed(2)}
          </p>
        </div>

        {order.payment_method == "Cash" && (
          <div className="text-right mb-4">
            <div className="text-right">
              <p className="font-bold text-[13px]">
                RECEIVED: Rs. {order.extras.cash_received?.toFixed(2)}
              </p>
            </div>
            <p className="font-bold text-[13px]">
              CHANGE: Rs.{" "}
              {(order.extras.cash_received - order.total_amount)?.toFixed(2)}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="border-b text-center mt-4">
          <p className="pb-2">
            # ITEMS SOLD: {order.order_items.length} TOTAL QTY SOLD:{" "}
            {totalQuantitySold}
          </p>
          <p className="font-semiBold text-[10]">
            Cashier: {order.createdBy.name}
          </p>
        </div>

        {/* Thank You Message */}
        <div className="border-b text-center mt-4">
          <p className="font-semiBold text-[10]">
            ***THANK YOU FOR SHOPPING WITH US!***
          </p>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={handlePrint}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md"
        >
          Print Receipt
        </button>
      </div>
    </>
  )
}

export default OrderDetails
